import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Activity, ChevronDown, ChevronUp, FlaskConical, RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { healthLabel } from "@/components/HealthPill";
import { HealthAlerts } from "./HealthAlerts";
import { Backups } from "./Backups";

const LABEL = { operational: "Operational", degraded: "Degraded", down: "Down", unknown: "Checking…", none: "No data" };
const TT = { fontSize: 12, fontFamily: "inherit", borderRadius: 6, background: "var(--tc-bg-elev)", border: "1px solid var(--tc-border)", color: "var(--tc-text)" };

const StatusPill = ({ status, testid }) => <span className={`tc-hstatus is-${status}`} data-testid={testid}><span className="tc-hstatus__dot" />{LABEL[status] || status}</span>;

const Uptime = ({ v }) => (v == null ? <span className="muted">—</span> : <span className={v < 99 ? "tc-uptime is-low" : "tc-uptime"}>{v.toFixed(v === 100 ? 0 : 2)}%</span>);

const DayBars = ({ days }) => (
  <div className="tc-daybars" title="Last 30 days">
    {days.map((d) => <span key={d.day} className={`tc-daybars__bar is-${d.status}`} title={`${d.day}: ${LABEL[d.status]}${d.total ? ` (${d.total} checks, ${d.down} down, ${d.degraded} degraded)` : ""}`} />)}
  </div>
);

const History = ({ id }) => {
  const [pts, setPts] = useState(null);
  const [hours, setHours] = useState(24);
  useEffect(() => { api.get("/admin/health/history", { params: { check: id, hours } }).then((r) => setPts(r.data.points)).catch(() => setPts([])); }, [id, hours]);
  const data = (pts || []).map((p) => ({ t: new Date(p.ts).getTime(), label: hours > 48 ? fmtDateTime(p.ts).slice(0, 6) : new Date(p.ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }), latency: p.latency_ms, down: p.status === "down" ? p.latency_ms || 1 : null, degraded: p.status === "degraded" ? p.latency_ms || 1 : null }));
  return (
    <div className="tc-hhistory" data-testid={`health-history-${id}`}>
      <div className="tc-hhistory__head">
        <span className="muted">Latency per check</span>
        <span className="grow" />
        {[6, 24, 168, 720].map((h) => <button key={h} className={`tc-seg${hours === h ? " is-on" : ""}`} onClick={() => setHours(h)}>{h < 48 ? `${h}h` : `${h / 24}d`}</button>)}
      </div>
      <div style={{ height: 160 }}>
        {pts && !pts.length ? <p className="muted" style={{ textAlign: "center", paddingTop: 60 }}>No data yet</p> : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="var(--tc-divider)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#616061" }} minTickGap={40} />
              <YAxis tick={{ fontSize: 10, fill: "#616061" }} unit="ms" />
              <Tooltip contentStyle={TT} formatter={(v, n) => [`${v} ms`, n]} />
              <Area type="monotone" dataKey="latency" stroke="#1264a3" fill="#1264a3" fillOpacity={0.12} strokeWidth={1.5} name="Latency" isAnimationActive={false} />
              <Area type="step" dataKey="degraded" stroke="#e8912d" fill="#e8912d" fillOpacity={0.5} strokeWidth={0} name="Degraded" isAnimationActive={false} />
              <Area type="step" dataKey="down" stroke="#e01e5a" fill="#e01e5a" fillOpacity={0.55} strokeWidth={0} name="Down" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

const CheckCard = ({ c, onSimulate }) => {
  const [open, setOpen] = useState(false);
  const meta = Object.entries(c.meta || {}).filter(([, v]) => v != null && typeof v !== "object").slice(0, 8);
  return (
    <div className={`tc-hcard is-${c.status}`} data-testid={`health-check-${c.id}`}>
      <div className="tc-hcard__top">
        <div className="tc-hcard__name">{c.name}{c.critical && <span className="tc-hcard__crit" title="Critical: if this is down the whole app counts as down">core</span>}{c.simulated && <span className="tc-hcard__sim" title="Result is simulated">simulated</span>}</div>
        <StatusPill status={c.status} testid={`health-status-${c.id}`} />
      </div>
      <div className="tc-hcard__detail" data-testid={`health-detail-${c.id}`}>{c.detail}</div>
      <DayBars days={c.days || []} />
      <div className="tc-hcard__foot">
        <span>30d <Uptime v={c.uptime_30d} /></span><span>7d <Uptime v={c.uptime_7d} /></span><span>24h <Uptime v={c.uptime_24h} /></span>
        <span className="grow" />
        <span className="muted">{c.latency_ms != null ? `${c.latency_ms} ms` : ""}{c.checked_at ? ` · ${fmtRelative(c.checked_at)}` : ""}</span>
      </div>
      {c.incident && <div className="tc-hcard__incident" data-testid={`health-incident-${c.id}`}>Incident since {fmtDateTime(c.incident.started_at)} · {c.incident.consecutive} consecutive failed check{c.incident.consecutive === 1 ? "" : "s"}{c.incident.alerted_at ? ` · SMS sent ${fmtRelative(c.incident.alerted_at)}` : c.incident.suppressed_at ? ` · SMS held — ${c.incident.hold_reason || "outside alert window"}` : ""}</div>}
      <div className="tc-hcard__actions">
        <button className="tc-linkbtn" onClick={() => setOpen((o) => !o)} data-testid={`health-toggle-${c.id}`}>{open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Hide" : "Details & history"}</button>
        <span className="grow" />
        <button className="tc-linkbtn muted" title="Force a simulated outage for 3 checks to test alerting" onClick={() => onSimulate(c)} data-testid={`health-simulate-${c.id}`}><FlaskConical size={12} /> Simulate</button>
      </div>
      {open && (
        <div className="tc-hcard__more">
          <p className="muted" style={{ margin: "0 0 8px" }}>{c.description}</p>
          {!!meta.length && <dl className="tc-hmeta">{meta.map(([k, v]) => <div key={k}><dt>{k.replace(/_/g, " ")}</dt><dd>{typeof v === "boolean" ? (v ? "yes" : "no") : typeof v === "number" ? v.toLocaleString() : String(v)}</dd></div>)}</dl>}
          <History id={c.id} />
        </div>
      )}
    </div>
  );
};

const Incidents = ({ rows }) => (
  <div className="tc-admin-card">
    <div className="tc-admin-card__head">Incidents<span className="right muted">{rows.filter((r) => !r.resolved_at).length} active</span></div>
    <div style={{ overflowX: "auto" }}>
    <table className="tc-table" data-testid="health-incidents">
      <thead><tr><th>Component</th><th>Status</th><th>Started</th><th>Duration</th><th>Alerts</th><th>Detail</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className={r.resolved_at ? "is-dim" : ""} data-testid={`incident-row-${r.id}`}>
            <td>{r.name}{r.simulated && <span className="tc-hcard__sim" style={{ marginLeft: 6 }}>simulated</span>}</td>
            <td>{r.resolved_at ? <span className="tc-pill">Resolved</span> : <StatusPill status={r.status} />}</td>
            <td className="muted">{fmtDateTime(r.started_at)}</td>
            <td className="muted">{r.resolved_at ? `${Math.max(1, Math.round(r.duration_sec / 60))} min` : `ongoing · ${r.consecutive} checks`}</td>
            <td className="muted">{r.sms_count ? `${r.sms_count} SMS` : r.suppressed_at ? `held — ${r.hold_reason || "outside window"}` : "—"}</td>
            <td className="muted" style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.detail}>{r.detail}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={6} className="muted" style={{ textAlign: "center", padding: 24 }}>No incidents recorded — nice.</td></tr>}
      </tbody>
    </table>
    </div>
  </div>
);

export const Health = () => {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "status";
  const [d, setD] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [h, i] = await Promise.all([api.get("/admin/health"), api.get("/admin/health/incidents", { params: { limit: 40 } })]);
      setD(h.data); setIncidents(i.data.incidents);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    const onPush = () => load();
    window.addEventListener("tc:health", onPush);
    return () => { clearInterval(iv); window.removeEventListener("tc:health", onPush); };
  }, [load]);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/health/run"); setD(data); toast.success(`Checked ${data.checks.length} components in ${data.last_duration_ms} ms`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const simulate = async (c) => {
    if (!window.confirm(`Simulate an outage of "${c.name}" for the next 3 checks (~3 min)?\n\nThis opens a real incident and, once it persists for ${d?.alerts?.consecutive || 2} checks, sends the SMS alert to all recipients — exactly like a real outage. It clears automatically.`)) return;
    try { const { data } = await api.post("/admin/health/simulate", { check_id: c.id, status: "down", rounds: 3 }); setD(data); toast.success(`Simulated outage started for ${c.name}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const counts = d ? d.checks.reduce((a, c) => ({ ...a, [c.status]: (a[c.status] || 0) + 1 }), {}) : {};
  const groups = d ? [...new Set(d.checks.map((c) => c.group))] : [];
  return (
    <div data-testid="admin-health">
      <h1>Health center</h1>
      <p className="lead">Every part of Total Chat is checked every {d?.interval || 60} seconds — from the database and realtime connections to email, webhooks, storage and the server itself. Problems open an incident and text your on-call numbers.</p>
      <div className="tc-tabs" data-testid="health-tabs">
        <button className={`tc-tab${tab === "status" ? " is-active" : ""}`} onClick={() => setSp({})} data-testid="health-tab-status">System status</button>
        <button className={`tc-tab${tab === "alerts" ? " is-active" : ""}`} onClick={() => setSp({ tab: "alerts" })} data-testid="health-tab-alerts">SMS alerts</button>
        <button className={`tc-tab${tab === "backups" ? " is-active" : ""}`} onClick={() => setSp({ tab: "backups" })} data-testid="health-tab-backups">Backups</button>
      </div>
      {tab === "alerts" ? <HealthAlerts /> : tab === "backups" ? <Backups /> : (
        <>
          <div className={`tc-hbanner is-${d?.overall || "unknown"}`} data-testid="health-banner" data-status={d?.overall || "unknown"}>
            <span className="tc-health-pill__dot" /><Activity size={16} />
            <strong data-testid="health-banner-label">{healthLabel(d)}</strong>
            <span className="muted">{d?.updated_at ? `Last checked ${fmtRelative(d.updated_at)} · round #${d.round} · ${d.last_duration_ms} ms` : ""}</span>
            <span className="grow" />
            {d && !d.alerts.configured && <span className="tc-hbanner__warn">SMS not configured</span>}
            {d && d.alerts.configured && !d.alerts.recipients && <button className="tc-hbanner__warn" onClick={() => setSp({ tab: "alerts" })} data-testid="health-no-recipients">No SMS recipients — add numbers</button>}
            {d && d.alerts.configured && !!d.alerts.recipients && <span className="muted">{d.alerts.sms_enabled ? `SMS to ${d.alerts.recipients} number${d.alerts.recipients === 1 ? "" : "s"}${d.alerts.in_window ? "" : " · outside alert window"}` : "SMS paused"}</span>}
            <button className="tc-btn tc-btn--outline sm" disabled={busy} onClick={run} data-testid="health-run-now"><RefreshCw size={13} className={busy ? "spin" : ""} /> {busy ? "Checking…" : "Run checks now"}</button>
          </div>
          <div className="tc-stats tc-stats--fit">
            <div className="tc-stat"><div className="tc-stat__label">Components</div><div className="tc-stat__value">{d?.checks.length ?? "—"}</div><div className="tc-stat__sub">{counts.operational || 0} operational · {counts.degraded || 0} degraded · {counts.down || 0} down</div></div>
            <div className="tc-stat"><div className="tc-stat__label">Active incidents</div><div className="tc-stat__value" data-testid="health-active-incidents">{incidents.filter((r) => !r.resolved_at).length}</div><div className="tc-stat__sub">{incidents.filter((r) => r.resolved_at).length} resolved recently</div></div>
            <div className="tc-stat"><div className="tc-stat__label">30-day uptime</div><div className="tc-stat__value">{d ? <Uptime v={Math.min(...d.checks.filter((c) => c.critical && c.uptime_30d != null).map((c) => c.uptime_30d), 100)} /> : "—"}</div><div className="tc-stat__sub">lowest across core components</div></div>
            <div className="tc-stat"><div className="tc-stat__label">Monitor</div><div className="tc-stat__value">{d?.interval ? `${d.interval}s` : "—"}</div><div className="tc-stat__sub">{d?.cron_last_at ? `cron watchdog ${fmtRelative(d.cron_last_at)}` : "cron watchdog every 15 min"}{d?.started_at ? ` · up since ${fmtDateTime(d.started_at)}` : ""}</div></div>
          </div>
          {groups.map((g) => (
            <section key={g} className="tc-hgroup">
              <h3 className="tc-hgroup__title">{g}</h3>
              <div className="tc-hgrid">{d.checks.filter((c) => c.group === g).map((c) => <CheckCard key={c.id} c={c} onSimulate={simulate} />)}</div>
            </section>
          ))}
          {!d && <p className="muted" style={{ padding: 24 }}>Loading health data…</p>}
          <Incidents rows={incidents} />
          <p className="muted" style={{ fontSize: 12 }}>Public URL being monitored: {d?.origin || "learning from first visit…"} · Integration API: <code>GET /api/v1/health</code> (scope <code>read:health</code>) · Public status page for staff: <a href="/status" target="_blank" rel="noreferrer" data-testid="health-status-page-link">{window.location.origin}/status</a> (no sign-in needed).</p>
        </>
      )}
    </div>
  );
};

